import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MovieList from '../components/MovieList';
import { useMovies } from '../hooks/useMovies';
import { useOrganizers } from '../hooks/useOrganizers';
import { getSubdomain, normalizeLabel } from '../lib/tenant';
import { Session } from '@supabase/supabase-js';
import { Movie } from '../types';

interface HomePageProps {
    session: Session | null;
    onBook: (movie: Movie) => void;
}

type HeroRotationMode = 'only_movies' | 'movies_and_organizers' | 'only_organizers';

export default function HomePage({ session, onBook }: HomePageProps) {
    const navigate = useNavigate();
    const { data: movies = [] } = useMovies();
    const { data: organizers = [] } = useOrganizers();

    const [heroIndex, setHeroIndex] = useState(0);
    const [heroOverlayIndex, setHeroOverlayIndex] = useState<number | null>(null);

    const subdomain = getSubdomain(window.location.hostname);
    const isTenant = !!subdomain;

    const tenantMovies = isTenant
        ? movies.filter(m => normalizeLabel(m.organizer || '') === normalizeLabel(subdomain || ''))
        : movies;

    const tenantOrganizers = isTenant
        ? organizers.filter(o => normalizeLabel(o.name) === normalizeLabel(subdomain || ''))
        : organizers;

    const tenantLogoUrl = isTenant && tenantOrganizers.length > 0
        ? (tenantOrganizers[0].logo_url || tenantOrganizers[0].image_url)
        : undefined;

    const heroRotationEnv = (import.meta.env.VITE_HERO_ROTATION_MODE as string | undefined) || 'only_movies';
    const heroMode: HeroRotationMode = heroRotationEnv === 'movies_and_organizers'
        ? 'movies_and_organizers'
        : heroRotationEnv === 'only_organizers'
            ? 'only_organizers'
            : 'only_movies';

    const tenantOrg = isTenant && tenantOrganizers.length > 0 ? tenantOrganizers[0] : null;
    const tenantGallery = tenantOrg?.gallery_images || [];
    const tenantGalleryEnabled = tenantOrg?.is_gallery_enabled !== false;
    const useTenantGallery = isTenant && tenantGallery.length > 0 && tenantGalleryEnabled;

    const heroItems = useTenantGallery
        ? tenantGallery.map((img, i) => ({ image_url: img, label: `Gallery ${i + 1}` }))
        : (
            heroMode === 'only_movies'
                ? tenantMovies.map(m => ({ image_url: m.image_url, label: m.title }))
                : heroMode === 'only_organizers'
                    ? tenantOrganizers.map(o => ({ image_url: o.image_url, label: o.name }))
                    : [...tenantMovies.map(m => ({ image_url: m.image_url, label: m.title })), ...tenantOrganizers.map(o => ({ image_url: o.image_url, label: o.name }))]
        );

    useEffect(() => {
        if (heroItems.length > 0) {
            const interval = setInterval(() => {
                const next = (heroIndex + 1) % heroItems.length;
                setHeroOverlayIndex(next);
                setTimeout(() => {
                    setHeroIndex(next);
                    setHeroOverlayIndex(null);
                }, 800);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [heroItems.length, heroIndex]);

    return (
        <>
            <div className="relative h-[600px] overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={heroItems.length > 0 ? heroItems[heroIndex].image_url : 'https://i.postimg.cc/02Y8y6Yt/Whats-App-Image-2025-03-24-at-13-08-06-1.jpg'}
                        alt={heroItems.length > 0 ? heroItems[heroIndex].label : 'Cinema'}
                        className="w-full h-full object-cover"
                    />
                    {heroOverlayIndex !== null && heroItems.length > 0 && (
                        <img
                            src={heroItems[heroOverlayIndex].image_url}
                            alt={heroItems[heroOverlayIndex].label}
                            className="w-full h-full object-cover absolute inset-0 animate-fade-in"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/50 to-white dark:from-gray-900/70 dark:via-gray-900/50 dark:to-gray-900"></div>
                    {tenantLogoUrl && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <img
                                src={tenantLogoUrl}
                                alt="Organizer Logo"
                                className="max-w-[45%] opacity-20 blur-sm object-contain"
                            />
                        </div>
                    )}
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
                    <div className="max-w-2xl animate-fade-in">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                            Experience Movies Like Never Before
                        </h1>
                        <p className="text-xl text-gray-700 dark:text-gray-200 mb-8">
                            Book your tickets now and immerse yourself in the magic of cinema.
                        </p>
                        <button
                            onClick={() => {
                                const moviesSection = document.getElementById('movies-section');
                                moviesSection?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="px-8 py-4 rounded-lg text-lg font-semibold bg-gray-800 text-white dark:bg-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 animate-glow"
                        >
                            Browse Movies
                        </button>
                    </div>
                </div>
            </div>

            <main id="movies-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 scroll-mt-24">
                <MovieList
                    movies={tenantMovies}
                    session={session}
                    onViewDetails={(id) => navigate(`/movie/${id}`)}
                    onBookMovie={onBook}
                />
            </main>
        </>
    );
}
